import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/sections/Footer";
import productsContent, { PRODUCTS } from "@/i18n/products";
import { useI18n } from "@/i18n/I18nProvider";
import { ArrowUpRight, ArrowRight, ShieldCheck, Sparkles, Check, ArrowLeft } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/motion/Reveal";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const p = productsContent[locale] || productsContent.en;
  const product = p.catalog[slug];

  useEffect(() => {
    if (product) document.title = `${product.name} — Smart Terminals`;
  }, [product]);

  if (!PRODUCTS.includes(slug) || !product) return <Navigate to="/products" replace />;

  const isTeal = product.accent === "teal";

  return (
    <main data-testid={`product-detail-page-${slug}`} className="bg-white text-ink-primary font-body">
      <Header />

      {/* Hero */}
      <section className={`relative pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden ${isTeal ? "bg-surface-dark text-white" : "bg-white"}`}>
        {isTeal ? <div className="absolute inset-0 dot-bg opacity-30" aria-hidden /> : <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />}
        <div className={`absolute -top-32 ${isTeal ? "left-[-10%]" : "right-[-10%]"} h-[480px] w-[480px] rounded-full ${isTeal ? "bg-brand-teal/20" : "bg-brand-red/10"} blur-3xl`} aria-hidden />

        <div className="container-pro relative">
          <a href="/products" className={`inline-flex items-center gap-2 text-xs font-medium ${isTeal ? "text-white/60 hover:text-white" : "text-ink-secondary hover:text-ink-primary"}`} data-testid="back-to-products">
            <ArrowLeft size={14} data-mirror-rtl="true" /> {p.common.backToProducts}
          </a>

          <div className="mt-8 flex items-center gap-4">
            <span className="relative inline-flex h-16 w-16 items-center justify-center">
              <span className={`absolute inset-0 rounded-xl ${isTeal ? "bg-brand-teal" : "bg-brand-black"}`} />
              <span className="absolute -right-1.5 -top-1.5 h-6 w-6 rounded-sm bg-brand-red" />
              <span className="relative">
                {isTeal ? <Sparkles size={26} className="text-white" /> : <ShieldCheck size={26} className="text-white" />}
              </span>
            </span>
            <div>
              <div className={`font-heading text-2xl md:text-3xl font-semibold ${isTeal ? "text-white" : "text-ink-primary"}`}>{product.name}</div>
              <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isTeal ? "text-white/40" : "text-ink-muted"}`}>Smart Terminals · {product.version}</div>
            </div>
          </div>

          <h1 className={`mt-8 h1-display text-4xl sm:text-5xl md:text-6xl max-w-3xl ${isTeal ? "text-white" : "text-ink-primary"}`}>
            <MaskedLine delay={0.15}>{product.tagline}</MaskedLine>
          </h1>
          <p className={`mt-5 max-w-2xl text-base md:text-lg leading-relaxed ${isTeal ? "text-white/70" : "text-ink-secondary"}`}>
            {product.short}
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {product.badges.map((b) => (
              <span
                key={b}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${
                  isTeal ? "bg-white/5 text-white/80 border-white/15" : "bg-white text-ink-secondary border-line"
                }`}
              >
                {b}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={product.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`detail-${slug}-demo`}
              className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded transition-colors ${
                isTeal ? "bg-brand-teal text-white hover:bg-brand-red" : "bg-brand-black text-white hover:bg-brand-red"
              }`}
            >
              {p.common.tryDemo} <ArrowUpRight size={16} />
            </a>
            <a
              href="/#contact"
              data-testid={`detail-${slug}-contact`}
              className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded border transition-colors ${
                isTeal ? "border-white/20 text-white hover:bg-white/10" : "border-ink-primary text-ink-primary hover:bg-ink-primary hover:text-white"
              }`}
            >
              {p.common.ctaContact} <ArrowRight size={16} data-mirror-rtl="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section className="section-pad bg-white relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
        <Reveal className="container-pro relative">
          <div className="overline">{p.common.keyModules}</div>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
            {product.name} — modules at a glance.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.modules.map((m) => (
              <div key={m.code} className="bento-card p-7 rounded-xl border border-line bg-white">
                <div className="flex items-center justify-between">
                  <span className="bento-icon-wrap inline-flex h-10 w-10 items-center justify-center rounded-md bg-surface-muted text-brand-red">
                    <ShieldCheck size={18} />
                  </span>
                  <span className="font-mono text-[10px] tracking-widest text-ink-muted">/ {m.code}</span>
                </div>
                <h3 className="mt-5 font-heading text-xl font-medium">{m.title}</h3>
                <p className="mt-3 text-sm text-ink-secondary leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Features */}
      <section className="section-pad bg-surface-muted relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
        <Reveal className="container-pro relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">{p.common.keyFeatures}</div>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-medium tracking-tight">
              {p.common.why}
            </h2>
          </div>
          <ul className="md:col-span-7 space-y-3">
            {product.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 bg-white border border-line rounded-lg p-4">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brand-red/10 text-brand-red mt-0.5">
                  <Check size={14} />
                </span>
                <span className="text-sm text-ink-primary leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Bonus + Who-for */}
      <section className="section-pad bg-white relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
        <Reveal className="container-pro relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">{p.common.whoFor}</div>
            <div className="mt-5 flex flex-col gap-3">
              {product.whoFor.map((w) => (
                <div key={w} className="flex items-start gap-3 border-l-2 border-brand-red ps-4">
                  <div className="text-sm text-ink-primary leading-relaxed">{w}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-7">
            {product.bonus ? (
              <a
                href={product.bonus.url}
                target="_blank"
                rel="noreferrer noopener"
                className="block p-8 rounded-xl border border-brand-red/30 bg-brand-red/5 hover:bg-brand-red/10 transition-colors"
                data-testid={`detail-${slug}-bonus`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-red">{product.bonus.title}</div>
                <div className="mt-2 font-heading text-2xl font-semibold text-ink-primary">{product.bonus.body}</div>
                <div className="mt-4 text-sm font-medium text-brand-red">{product.bonus.label}</div>
              </a>
            ) : (
              <div className="p-8 rounded-xl border border-line bg-surface-muted">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-red">{p.common.cta}</div>
                <p className="mt-3 text-base text-ink-secondary">{p.common.ctaBody}</p>
                <a href={product.url} target="_blank" rel="noreferrer noopener" className="btn-primary mt-5 inline-flex" data-testid={`detail-${slug}-side-demo`}>
                  {p.common.tryDemo} <ArrowUpRight size={14} />
                </a>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
